/**
 * FLEET MESSAGES SCREEN
 *
 * Fleet manager inbox: lists each fleet job that has a conversation, with
 * preview + unread count. Tapping a row opens the existing MessagingScreen
 * with onBehalfOfDriverId set so replies are attributed to the fleet driver
 * actually assigned to that job.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, FlatList, TouchableOpacity, ActivityIndicator, RefreshControl, SafeAreaView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../services/api';
import { RD } from '../../theme/colors';

type ThreadSummary = {
  role: 'BUYER' | 'SELLER' | 'AGENT';
  lastTimestamp: string | null;
  lastText: string | null;
  unread: number;
};
type FleetInboxRow = {
  jobId: string;
  wooOrderId: string | null;
  productTitle: string | null;
  status: string;
  assignedFleetDriverId: string | null;
  assignedFleetDriverName: string | null;
  lastMessageAt: string | null;
  lastMessageText: string | null;
  lastMessageSenderType: string | null;
  unread: number;
  threads: ThreadSummary[];
};

function fmtRelative(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function FleetMessagesScreen({ navigation }: any) {
  const [rows, setRows] = useState<FleetInboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/messages/fleet-inbox');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Re-fetch on focus too — otherwise a reassignment done in FleetScreen leaves this still-
  // mounted inbox showing the OLD assignedFleetDriverId, so "reply on behalf" would attribute
  // the reply to the wrong fleet driver.
  useEffect(() => {
    load();
    const unsub = navigation.addListener('focus', load);
    return unsub;
  }, [load, navigation]);

  const onRefresh = () => { setRefreshing(true); load(); };

  const openThread = (row: FleetInboxRow) => {
    // Pick the recipient with the most recent message; default BUYER.
    const sorted = [...row.threads]
      .filter(t => t.lastTimestamp)
      .sort((a, b) => new Date(b.lastTimestamp!).getTime() - new Date(a.lastTimestamp!).getTime());
    const initialRecipient = (sorted[0]?.role || 'BUYER') as 'BUYER' | 'SELLER' | 'AGENT';
    navigation.navigate('Messaging', {
      jobId: row.jobId,
      job: { id: row.jobId, status: row.status, productTitle: row.productTitle },
      initialRecipient,
      onBehalfOfDriverId: row.assignedFleetDriverId || undefined,
      onBehalfOfDriverName: row.assignedFleetDriverName || undefined,
    });
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: RD.bg }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, backgroundColor: RD.surface, borderBottomWidth: 1, borderColor: RD.border }}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ padding: 4, marginRight: 8, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' }} activeOpacity={0.7} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <Text style={{ fontSize: 19, fontWeight: '700', color: RD.text, flex: 1 }}>Fleet Inbox</Text>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color={RD.maroon} style={{ marginTop: 40 }} />
      ) : rows.length === 0 ? (
        <View style={{ padding: 32, alignItems: 'center' }}>
          <Ionicons name="chatbubbles-outline" size={48} color={RD.g30} />
          <Text style={{ marginTop: 12, fontSize: 15, color: RD.textMuted, textAlign: 'center', lineHeight: 21 }}>
            No fleet conversations yet.{'\n'}Messages on your fleet's jobs will show up here.
          </Text>
        </View>
      ) : (
        <FlatList
          data={rows}
          keyExtractor={r => r.jobId}
          contentContainerStyle={{ padding: 16, gap: 10 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={RD.maroon} />}
          renderItem={({ item }) => (
            <TouchableOpacity
              onPress={() => openThread(item)}
              style={{ padding: 16, borderRadius: 18, borderWidth: 1, borderColor: item.unread > 0 ? RD.borderStrong : RD.border, backgroundColor: item.unread > 0 ? RD.maroonSoft : RD.surface, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 }}
              activeOpacity={0.7}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 15, fontWeight: item.unread > 0 ? '700' : '600', color: RD.text, flex: 1 }} numberOfLines={1}>
                  {item.productTitle || `Order #${item.wooOrderId || item.jobId.slice(0, 6)}`}
                </Text>
                <Text style={{ fontSize: 12, color: RD.textFaint, marginLeft: 8 }}>{fmtRelative(item.lastMessageAt)}</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                <Text style={{ fontSize: 12, color: RD.textMuted }}>
                  {item.assignedFleetDriverName ? `Driver: ${item.assignedFleetDriverName}` : 'Unassigned'}
                  {item.status ? ` · ${item.status.replace(/_/g, ' ')}` : ''}
                </Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6 }}>
                <Text style={{ fontSize: 13, color: RD.textSec, flex: 1 }} numberOfLines={1}>
                  {item.lastMessageSenderType ? `[${item.lastMessageSenderType}] ` : ''}{item.lastMessageText || ''}
                </Text>
                {item.unread > 0 && (
                  <View style={{ minWidth: 20, height: 20, paddingHorizontal: 6, borderRadius: 999, backgroundColor: RD.maroon, alignItems: 'center', justifyContent: 'center', marginLeft: 8 }}>
                    <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>{item.unread}</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          )}
        />
      )}
    </SafeAreaView>
  );
}
